import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Trash2, Mail, Smartphone } from "lucide-react";
import { Logo } from "@/components/Logo";

const EMAIL = "BigDog@happybigdogs.net";

export default function DeleteAccount() {
  const navigate = useNavigate();
  const mailto = `mailto:${EMAIL}?subject=${encodeURIComponent(
    "Account deletion request — Wooflect"
  )}&body=${encodeURIComponent(
    "Please delete my Wooflect account and associated data.\n\nAccount email: \n(We will verify ownership before deleting.)"
  )}`;

  return (
    <div className="min-h-screen bg-[#FCFBF8] text-[#2D2B2A]">
      <header className="sticky top-0 z-10 backdrop-blur-xl bg-[#FCFBF8]/85 border-b border-[#E8E3DA]">
        <div className="max-w-2xl mx-auto px-5 h-16 flex items-center justify-between">
          <button
            data-testid="delete-account-back-btn"
            onClick={() => navigate(-1)}
            className="h-9 w-9 rounded-full bg-[#F2EFE9] flex items-center justify-center"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="font-display font-extrabold">Delete Your Account</span>
          <Logo size={28} withWordmark={false} to="/" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-5 py-8" data-testid="delete-account-screen">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-[#FBE4DC] text-[#D9534F] flex items-center justify-center shrink-0">
            <Trash2 size={24} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-black leading-tight">Delete your Wooflect account</h1>
            <p className="text-sm text-[#9A938B] mt-1">Happy Big Dogs · Wooflect</p>
          </div>
        </div>

        <p className="text-[#4A4746] leading-relaxed mt-6">
          You can permanently delete your Wooflect account and all associated personal data at any time.
          Deletion is immediate and cannot be undone.
        </p>

        {/* In-app method */}
        <div className="mt-6 rounded-3xl bg-white border border-[#E8E3DA] p-5">
          <h2 className="font-display font-extrabold text-lg flex items-center gap-2">
            <Smartphone size={18} className="text-[#6F8F72]" /> Delete from inside the app
          </h2>
          <ol className="list-decimal ml-5 mt-3 space-y-1.5 text-[#4A4746] leading-relaxed">
            <li>Open the Wooflect app and sign in.</li>
            <li>Go to the <span className="font-semibold">Profile</span> tab.</li>
            <li>Scroll to the bottom and tap <span className="font-semibold">Delete account</span>.</li>
            <li>Confirm. Your account and data are removed right away.</li>
          </ol>
        </div>

        {/* Email method */}
        <div className="mt-4 rounded-3xl bg-white border border-[#E8E3DA] p-5">
          <h2 className="font-display font-extrabold text-lg flex items-center gap-2">
            <Mail size={18} className="text-[#6F8F72]" /> Request deletion by email
          </h2>
          <p className="text-[#4A4746] leading-relaxed mt-2">
            Can&rsquo;t access the app? Email us from the address on your account and we&rsquo;ll delete it for you
            (we verify ownership first).
          </p>
          <a
            data-testid="delete-account-email-btn"
            href={mailto}
            className="inline-flex items-center gap-2 mt-4 h-11 px-5 rounded-full bg-[#D9534F] text-white font-bold hover:bg-[#C44742]"
          >
            <Mail size={17} /> Email {EMAIL}
          </a>
        </div>

        {/* What gets deleted */}
        <div className="mt-6">
          <h2 className="font-display font-extrabold text-lg">What gets deleted</h2>
          <p className="text-[#4A4746] leading-relaxed mt-1">
            Your profile (email, name), your dogs&rsquo; profiles (names, breeds, photos, notes), your AI chat
            history and scans, reminders, and notification settings are permanently removed. Payment records
            are handled by Stripe and retained only as required for tax and legal compliance; we never store
            your card number.
          </p>
          <p className="text-xs italic text-[#9A938B] bg-[#F2EFE9] rounded-2xl p-4 mt-5">
            If you have an active paid subscription, cancel it before deleting to avoid future charges, or
            email {EMAIL} and we&rsquo;ll handle cancellation as part of your deletion request. Questions? Reach us
            at {EMAIL}.
          </p>
        </div>
      </div>
    </div>
  );
}
